/**
 * AddressForm — Add / Edit a delivery address
 * Fixed: ADDRESS_TYPES defined inline — no external import dependency
 * Fixed: All .map() calls are null-safe
 */

import React, { useEffect, useState } from "react";
import {
  HomeOutlined,
  BankOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import { Button, Col, Form, Input, Row, theme, App } from "antd";
import { addressAPI } from "../../api/services";

// ─── Defined inline — no external import, never undefined ────────────────────
const ADDRESS_TYPES = [
  { id: "home",  label: "Home",  Icon: HomeOutlined },
  { id: "work",  label: "Work",  Icon: BankOutlined },
  { id: "other", label: "Other", Icon: EnvironmentOutlined },
];

const AddressForm = ({ isAddressForm, onClose }) => {
  const { token } = theme.useToken();
  const { notification } = App.useApp();
  const [form] = Form.useForm();
  const [selectedAddressType, setSelectedAddressType] = useState("home");
  const [loading, setLoading] = useState(false);

  // ─── Pre-fill when editing ─────────────────────────────────────────────────
  useEffect(() => {
    if (isAddressForm && Object.keys(isAddressForm).length) {
      form.setFieldsValue(isAddressForm);
      setSelectedAddressType(isAddressForm.address_type || "home");
    } else {
      form.resetFields();
      setSelectedAddressType("home");
    }
  }, [isAddressForm, form]);

  // ─── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = normalizePayload({ ...values, address_type: selectedAddressType });

      if (isAddressForm?.uuid) {
        await addressAPI.update(isAddressForm.uuid, payload);
        notification.success({ message: "Address updated successfully!" });
      } else {
        await addressAPI.create(payload);
        notification.success({ message: "Address added successfully!" });
      }
      onClose();
    } catch (err) {
      notification.error({
        message: err?.message || "Failed to save address. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      size="large"
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      style={{ marginBottom: 24 }}
    >
      {/* ── Address Line ──────────────────────────────────────────────────── */}
      <Form.Item
        label="House No, Building, Road, Area *"
        name="address_line1"
        rules={[{ required: true, message: "Please enter your address" }]}
      >
        <Input style={inputStyle} placeholder="e.g. 821/22 Gandhi Nagar, Gali No. 5" />
      </Form.Item>

      {/* ── Pincode / City / State ────────────────────────────────────────── */}
      <Row gutter={[12, 0]}>
        <Col xs={24} sm={8}>
          <Form.Item
            label="Pincode *"
            name="postal_code"
            rules={[
              { required: true, message: "Required" },
              { pattern: /^\d{6}$/, message: "Enter valid 6-digit pincode" },
            ]}
          >
            <Input style={inputStyle} maxLength={6} type="tel" placeholder="110001" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item
            label="City *"
            name="city"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input style={inputStyle} placeholder="New Delhi" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item
            label="State *"
            name="state"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input style={inputStyle} placeholder="Delhi" />
          </Form.Item>
        </Col>
      </Row>

      {/* ── Recipient ─────────────────────────────────────────────────────── */}
      <div style={sectionTitle}>RECIPIENT</div>

      <Form.Item
        label="Phone Number *"
        name="phone"
        rules={[
          { required: true, message: "Required" },
          { pattern: /^[6-9]\d{9}$/, message: "Enter valid 10-digit number" },
        ]}
      >
        <Input type="tel" style={inputStyle} placeholder="9911522174" />
      </Form.Item>

      <Form.Item
        label="Full Name *"
        name="full_name"
        rules={[{ required: true, message: "Required" }]}
      >
        <Input style={inputStyle} placeholder="Rahul Jangra" />
      </Form.Item>

      <Form.Item
        label="Email *"
        name="email"
        rules={[
          { required: true, message: "Required" },
          { type: "email", message: "Enter a valid email" },
        ]}
      >
        <Input type="email" style={inputStyle} placeholder="rahul@example.com" />
      </Form.Item>

      {/* ── Save As ───────────────────────────────────────────────────────── */}
      <div style={sectionTitle}>SAVE AS</div>

      <Row gutter={[8, 8]} style={{ marginBottom: 20 }}>
        {ADDRESS_TYPES.map(({ id, label, Icon }) => {
          const isSelected = selectedAddressType === id;
          return (
            <Col xs={8} key={id}>
              <div
                onClick={() => setSelectedAddressType(id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  padding: "8px 6px",
                  borderRadius: 8,
                  border: `1.5px solid ${isSelected ? token.colorPrimary : "#e0e0e0"}`,
                  backgroundColor: isSelected ? `${token.colorPrimary}15` : "#fafafa",
                  color: isSelected ? token.colorPrimary : "#666",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  fontSize: 13,
                  minHeight: 40,
                  fontWeight: isSelected ? 600 : 400,
                }}
              >
                <Icon style={{ fontSize: 14 }} />
                <span>{label}</span>
              </div>
            </Col>
          );
        })}
      </Row>

      {/* ── Terms ─────────────────────────────────────────────────────────── */}
      <p style={{ fontSize: 11, color: "#aaa", lineHeight: 1.6, marginBottom: 20 }}>
        This address will be secured with OTP on future checkouts.{" "}
        <a href="#" style={{ color: "#555" }}>Terms</a> &{" "}
        <a href="#" style={{ color: "#555" }}>Privacy Policy</a>
      </p>

      {/* ── Submit ────────────────────────────────────────────────────────── */}
      <Button
        type="primary"
        size="large"
        htmlType="submit"
        block
        loading={loading}
        style={{ height: 48, borderRadius: 8, fontWeight: 600, fontSize: 15 }}
      >
        Save & Continue
      </Button>
    </Form>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const normalizePayload = (values) => ({
  postal_code:    values.postal_code,
  pincode:        values.postal_code,
  city:           values.city,
  state:          values.state,
  full_name:      values.full_name,
  email:          values.email,
  phone:          values.phone,
  address_line_1: values.address_line1,
  address_line_2: values.address_line1,
  address_line1:  values.address_line1,
  address_line2:  values.address_line1,
  landmark:       values.landmark,
  address_type:   values.address_type || "home",
});

const inputStyle = {
  height: 48,
  borderRadius: 8,
  border: "1px solid #d0d0d0",
  fontSize: 15,
};

const sectionTitle = {
  fontSize: 11,
  fontWeight: 700,
  color: "#aaa",
  letterSpacing: "0.8px",
  marginBottom: 12,
  marginTop: 4,
};

export default AddressForm;